import type { ColorSystemOptions, PaletteColor, SupportedColorScheme, ThemeOptions, TypeAction } from '@mui/material/styles';

import { createPaletteChannel, varAlpha } from 'minimal-shared/utils';

import { alpha, darken, lighten, rgbToHex } from '@mui/material/styles';

import { getParentBackgroundColor } from '../utils/tools/getParentBackgroundColor';
import { customShadows } from './custom-shadows';

import type { ConfigObject } from '../_configs/types';

// ----------------------------------------------------------------------

export type ThemeColorScheme = SupportedColorScheme;
export type SchemesRecord<T> = Partial<Record<ThemeColorScheme, T>>;

declare module '@mui/material/styles' {
    interface CommonColors {
        whiteChannel: string;
        blackChannel: string;
    }
    interface Grey {
        '50Channel': string;
        '100Channel': string;
        '200Channel': string;
        '300Channel': string;
        '400Channel': string;
        '500Channel': string;
        '600Channel': string;
        '700Channel': string;
        '800Channel': string;
        '900Channel': string;
    }
    interface PaletteColor {
        lighter: string;
        darker: string;
        lighterChannel: string;
        darkerChannel: string;
    }
    interface TypeBackground {
        neutral: string;
        neutralChannel: string;
    }
    interface TypeText {
        disabledChannel: string;
    }
}

/**
 * ➤
 * ➤ ➤ Core palette (primary, secondary, info, success, warning, error, common, grey)
 * ➤
 */
export const grey = createPaletteChannel({
    0: '#FFFFFF',
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#212B36',
    900: '#161C24',
});
export const primary = createPaletteChannel({
    lighter: '#C8FAD6',
    light: '#5BE49B',
    main: '#00A76F',
    dark: '#007867',
    darker: '#004B50',
    contrastText: '#FFFFFF',
});
export const secondary = createPaletteChannel({
    lighter: '#EFD6FF',
    light: '#C684FF',
    main: '#8E33FF',
    dark: '#5119B7',
    darker: '#27097A',
    contrastText: '#FFFFFF',
});
export const info = createPaletteChannel({
    lighter: '#CAFDF5',
    light: '#61F3F3',
    main: '#00B8D9',
    dark: '#006C9C',
    darker: '#003768',
    contrastText: '#FFFFFF',
});
export const success = createPaletteChannel({
    lighter: '#D3FCD2',
    light: '#77ED8B',
    main: '#22C55E',
    dark: '#118D57',
    darker: '#065E49',
    contrastText: '#ffffff',
});
export const warning = createPaletteChannel({
    lighter: '#FFF5CC',
    light: '#FFD666',
    main: '#FFAB00',
    dark: '#B76E00',
    darker: '#7A4100',
    contrastText: grey[800],
});
export const error = createPaletteChannel({
    lighter: '#FFE9D5',
    light: '#FFAC82',
    main: '#FF5630',
    dark: '#B71D18',
    darker: '#7A0916',
    contrastText: '#FFFFFF',
});
export const common = createPaletteChannel({
    black: '#000000',
    white: '#FFFFFF',
});

/**
 * ➤
 * ➤ ➤ Text, background, action
 * ➤
 */
export const text = {
    light: createPaletteChannel({ primary: grey[800], secondary: grey[600], disabled: grey[500] }),
    dark: createPaletteChannel({ primary: '#FFFFFF', secondary: grey[500], disabled: grey[600] }),
};

export const background = {
    light: createPaletteChannel({ paper: '#FFFFFF', default: '#FFFFFF', neutral: grey[200] }),
    dark: createPaletteChannel({ paper: grey[800], default: grey[900], neutral: '#28323D' }),
};

export const action = (mode: 'light' | 'dark'): Partial<TypeAction> => ({
    hover: varAlpha(grey['500Channel'], 0.08),
    selected: varAlpha(grey['500Channel'], 0.16),
    focus: varAlpha(grey['500Channel'], 0.24),
    disabled: varAlpha(grey['500Channel'], 0.8),
    disabledBackground: varAlpha(grey['500Channel'], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    active: mode === 'light' ? grey[600] : grey[500],
});

/**
 * ➤
 * ➤ ➤ Base configuration
 * ➤
 */
const basePalette: ColorSystemOptions['palette'] = {
    primary,
    secondary,
    info,
    success,
    warning,
    error,
    common,
    grey,
    divider: varAlpha(grey['500Channel'], 0.2),
};

// ----------------------------------------------------------------------

function generateColorPalette(color: string): Omit<PaletteColor, 'contrastText' | 'lighterChannel' | 'darkerChannel'> {
    return {
        lighter: rgbToHex(lighten(color, 0.2)),
        light: rgbToHex(lighten(color, 0.1)),
        main: color,
        dark: rgbToHex(darken(color, 0.1)),
        darker: rgbToHex(darken(color, 0.2)),
    };
}

/* **********************************************************************
 * 📦 Final
 * **********************************************************************/
export function palette(
    mode: 'light' | 'dark',
    rootContainer: HTMLDivElement,
    primaryColor: ConfigObject['style']['primaryColor'],
    secondaryColor: ConfigObject['style']['secondaryColor']
): ThemeOptions {
    const primary = createPaletteChannel(generateColorPalette(primaryColor));
    const secondary = createPaletteChannel(generateColorPalette(secondaryColor));
    const parentBackgroundColor = getParentBackgroundColor(rootContainer || undefined);

    return {
        customShadows: {
            ...customShadows(mode),
            primary: `0 8px 16px 0 ${alpha(`${primary?.main}`, 0.24)}`,
            secondary: `0 8px 16px 0 ${alpha(`${secondary?.main}`, 0.24)}`,
        },
        palette:
            mode === 'light'
                ? {
                      ...basePalette,
                      primary,
                      secondary,
                      text: text.light,
                      background: background.light,
                      action: action('light'),
                  }
                : {
                      ...basePalette,
                      primary,
                      secondary,
                      text: text.dark,
                      background: background.dark,
                      action: action('dark'),
                  },
        parentBackgroundColor,
        rawTypography: undefined,
    };
}
